import axios from 'axios'

const state = {
  blockedOrders: [],
  // активные блокировки текущего пользователя (BlockedOrderInfoDto: orderId, filialId, filialName,
  // blockedDate, unblockDate, secondsLeft) — красный замок, таймер 48 ч, тусклые филиалы в оптимизаторе
  myBlockedOrders: [],
  // Date.now() на момент ответа бэка: обратный отсчёт считается от secondsLeft сервера,
  // чтобы не зависеть от часового пояса браузера
  myBlockedOrdersFetchedAt: null,
  isLoading: false,
  error: null
}

const mutations = {
  SET_BLOCKED_ORDERS(state, data) {
    state.blockedOrders = data
  },
  SET_MY_BLOCKED_ORDERS(state, data) {
    state.myBlockedOrders = data
    state.myBlockedOrdersFetchedAt = Date.now()
  },
  SET_LOADING(state, value) {
    state.isLoading = value
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  }
}

// Вспомогательная функция для извлечения сообщения об ошибке
function extractErrorMessage(err, defaultMessage) {
  if (err.response?.data?.error) {
    return err.response.data.error
  }
  if (err.response?.data?.message) {
    return err.response.data.message
  }
  if (err.response?.data?.title) {
    return err.response.data.title
  }
  if (typeof err.response?.data === 'string') {
    return err.response.data
  }
  return defaultMessage
}

function showSnackbar(commit, text, color) {
  commit("SET_SNACKBAR", {
    text,
    timeout: color === "error" ? 7500 : 5000,
    color,
    linkText: "",
    show: true,
  }, { root: true })
}

function secondsUntilUnblock(state, info, now) {
  const fetchedAt = state.myBlockedOrdersFetchedAt || now
  const elapsed = Math.floor((now - fetchedAt) / 1000)
  return Math.max(0, (info.secondsLeft || 0) - elapsed)
}

const actions = {
  async fetchBlockedOrders({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await axios.get('/BlockedOrder/GetAllAsync')
      commit('SET_BLOCKED_ORDERS', response.data)
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Ошибка при загрузке заблокированных заказов')
      commit('SET_ERROR', errorMessage)
      showSnackbar(commit, errorMessage, "error")
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // активные блокировки текущего пользователя; бэк заодно закрывает его просроченные (старше 48 ч) блокировки.
  // Вызывается при входе в потребность (загрузка страницы), после попытки отправки заказа и в момент,
  // когда счётчик на фронте дошёл до нуля (проверка: снять блокировку может только ответ бэка).
  // Периодического опроса нет. Возвращает true, если список получен.
  async fetchMyBlockedOrders({ state, commit }) {
    try {
      const response = await axios.get('/Order/GetMyBlockedOrdersAsync')
      commit('SET_MY_BLOCKED_ORDERS', response.data || [])
      return true
    } catch (err) {
      // первая загрузка не удалась — работаем без замков (флаг не критичен);
      // повторная (проверка по таймеру) — оставляем прежние блокировки, снимать их без ответа бэка нельзя
      if (state.myBlockedOrdersFetchedAt === null) {
        commit('SET_MY_BLOCKED_ORDERS', [])
      }
      return false
    }
  },

  async acceptOrder({ commit, dispatch }, { orderId, orderDate }) {
    try {
      await axios.post('/BlockedOrder/AcceptAsync', null, { params: { orderId, orderDate } })
      showSnackbar(commit, `Заказ №${orderId} принят (отмечен отправленным)`, "success")
      await dispatch('fetchBlockedOrders')
    } catch (err) {
      showSnackbar(commit, extractErrorMessage(err, 'Ошибка при принятии заказа'), "error")
      throw err
    }
  },

  async unblockOrder({ commit, dispatch }, orderId) {
    try {
      await axios.delete('/BlockedOrder/UnblockAsync', { params: { orderId } })
      showSnackbar(commit, `Заказ №${orderId} разблокирован`, "success")
      await dispatch('fetchBlockedOrders')
    } catch (err) {
      showSnackbar(commit, extractErrorMessage(err, 'Ошибка при разблокировке заказа'), "error")
      throw err
    }
  },

  async blockOrder({ commit, dispatch }, orderId) {
    try {
      await axios.post('/BlockedOrder/BlockAsync', null, { params: { orderId } })
      showSnackbar(commit, `Заказ №${orderId} заблокирован`, "success")
      await dispatch('fetchBlockedOrders')
    } catch (err) {
      showSnackbar(commit, extractErrorMessage(err, 'Ошибка при блокировке заказа'), "error")
      throw err
    }
  }
}

const getters = {
  isOrderBlocked: (state) => (orderId) =>
    state.myBlockedOrders.some(x => x.orderId == orderId),

  blockedOrderInfo: (state) => (orderId) =>
    state.myBlockedOrders.find(x => x.orderId == orderId) || null,

  // филиалы, по которым у пользователя есть заблокированный заказ: их нельзя выбирать в оптимизаторе
  blockedFilialIds: (state) =>
    [...new Set(state.myBlockedOrders.map(x => x.filialId))],

  blockedOrderByFilial: (state) => (filialId) =>
    state.myBlockedOrders.find(x => x.filialId == filialId) || null,

  // секунд до авторазблокировки на момент `now` (для таймера в потребности)
  secondsUntilUnblock: (state) => (orderId, now = Date.now()) => {
    const info = state.myBlockedOrders.find(x => x.orderId == orderId)
    return info ? secondsUntilUnblock(state, info, now) : 0
  },

  // список уже загружали в этой сессии (чтобы вкладка «Хранилище» не дёргала бэк повторно)
  myBlockedOrdersLoaded: (state) => state.myBlockedOrdersFetchedAt !== null
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
